import React, { useEffect, useState } from "react";
import { User, Save, Loader2 } from "lucide-react";
import { Card, Badge, SectionLabel } from "../components/ui-bits";
import { useAuth, formatApiError } from "../auth/AuthContext";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Settings() {
  const { user, updateProfile } = useAuth();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ full_name: "", email: "" });

  // Hydrate the form when the user object loads (initial mount + on auth refresh).
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const initials =
    (fullName || user?.full_name || "")
      .split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "FI";

  const dirty =
    (user && ((fullName || "").trim() !== (user.full_name || "")
      || (email || "").trim().toLowerCase() !== (user.email || "")));

  const validate = () => {
    const next = { full_name: "", email: "" };
    if ((fullName || "").trim().length < 2) next.full_name = "Full name must be at least 2 characters.";
    if (!EMAIL_RE.test((email || "").trim())) next.email = "Please enter a valid email address.";
    setErrors(next);
    return !next.full_name && !next.email;
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await updateProfile({
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
      });
      toast.success("Profile updated successfully.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <User size={14} className="text-fij-green" />
          <SectionLabel>Profile</SectionLabel>
        </div>

        <form onSubmit={onSave} noValidate className="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-4 sm:gap-6">
          <div
            data-testid="settings-avatar"
            className="w-16 h-16 rounded-sm bg-fij-green/10 border border-fij-green/30 grid place-items-center text-fij-green font-mono text-xl"
          >
            {initials}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <SectionLabel>Full Name</SectionLabel>
              <input
                data-testid="settings-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
                className="mt-1 w-full bg-fij-bg border border-fij-border rounded-sm px-3 py-2 font-mono text-sm text-fij-text outline-none focus:border-fij-green"
              />
              {errors.full_name && (
                <div data-testid="settings-name-error" className="mt-1 font-mono text-[11px] text-fij-red">
                  {errors.full_name}
                </div>
              )}
            </div>

            <div>
              <SectionLabel>Email Address</SectionLabel>
              <input
                data-testid="settings-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="mt-1 w-full bg-fij-bg border border-fij-border rounded-sm px-3 py-2 font-mono text-sm text-fij-text outline-none focus:border-fij-green"
              />
              {errors.email && (
                <div data-testid="settings-email-error" className="mt-1 font-mono text-[11px] text-fij-red">
                  {errors.email}
                </div>
              )}
            </div>

            <div>
              <SectionLabel>Membership</SectionLabel>
              <div className="mt-2" data-testid="settings-member-badge">
                <Badge tone="positive">FIJ Member</Badge>
              </div>
            </div>

            <div>
              <SectionLabel>Time Zone</SectionLabel>
              <div
                data-testid="settings-timezone"
                className="mt-1 font-mono text-sm text-fij-text"
                title="Read-only"
              >
                Asia/Kolkata (IST)
              </div>
            </div>

            <div className="sm:col-span-2 flex items-center justify-end pt-2">
              <button
                type="submit"
                disabled={saving || !dirty}
                data-testid="settings-save"
                className="inline-flex items-center gap-2 rounded-sm px-3.5 py-2 font-mono text-xs uppercase tracking-wider border border-fij-green/40 text-fij-green bg-fij-green/10 hover:bg-fij-green/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
